/* tslint:disable:no-forward-ref max-file-line-count */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  forwardRef,
  Input,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { TimepickerConfig } from './timepicker.config';
import {
  TimepickerComponentState,
  TimepickerControls
} from './timepicker.models';
import {
  isInputValid,
  isValidDate,
  padNumber,
  parseTime
} from './timepicker.utils';
import { TimepickerService } from './timepicker.shared.service';

export const TIMEPICKER_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TimepickerInnerComponent),
  multi: true
};

export const TIMEPICKER_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => TimepickerInnerComponent),
  multi: true
};

@Component({
  selector: 'axis-inner-timepicker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TIMEPICKER_CONTROL_VALUE_ACCESSOR, TIMEPICKER_VALIDATORS],
  templateUrl: 'timepicker-inner.component.html'
})
export class TimepickerInnerComponent
  implements
  ControlValueAccessor,
  TimepickerComponentState,
  TimepickerControls {

  @Input() arrowkeys: boolean; // if false up/down arrowkeys inside hours,minutes,seconds will be disabled.
  @Input() hourStep: number;  // hours change step.
  @Input() minuteStep: number;  // minutes change step.
  @Input() secondsStep: number;  // seconds change step.

  @Input() showSpinners: boolean;   // if true spinner arrows above and below the inputs will be shown.
  @Input() showMeridian: boolean;
  @Input() showSeconds: boolean;

  @Input() meridians: string[];   // meridian labels based on locale.

  @Input() min: Date;   // minimum time user can select.
  @Input() max: Date;   // maximum time user can select.

  @Input()
  set activeTime(val: any) {
    this._renderTime(val);
    this.validateTime(val);
  }
  /** emits true if value is a valid date */
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();
  @Output() public selectionDone: EventEmitter<any> = new EventEmitter<any>(undefined);
  @Output() public closeEvent: EventEmitter<any> = new EventEmitter<any>(undefined);

  // ui variables
  hours: string;
  _hours: string;
  minutes: string;
  seconds: string;
  meridian: string;
  modelValue: Date;
  prevHourVal: any;
  prevMinVal: any;
  prevSecVal: any;

  get isSpinnersVisible(): boolean {
    return this.showSpinners;
  }

  // min\max validation for input fields
  invalidHours = false;
  invalidMinutes = false;
  invalidSeconds = false;

  // time picker controls state
  canIncrementHours: boolean;
  canIncrementMinutes: boolean;
  canIncrementSeconds: boolean;

  canDecrementHours: boolean;
  canDecrementMinutes: boolean;
  canDecrementSeconds: boolean;

  // control value accessor methods
  onChange: any = Function.prototype;
  onTouched: any = Function.prototype;

  constructor(_config: TimepickerConfig, private _cd: ChangeDetectorRef, private timepickerService: TimepickerService) {
    Object.assign(this, _config);
  }

  isPM(): boolean {
    return this.showMeridian && this.meridian === this.meridians[1];
  }

  updateMeridian(hours, prevValue, step) {
    if ((parseInt(prevValue, 10) < 12 && hours >= 12 && step > 0) || (
      (parseInt(prevValue, 10) >= 1 &&
        hours < 1 &&
        12 + hours !== 12 &&
        step < 0) ||
      (parseInt(prevValue, 10) === 12 && step < 0)
    )) {
      this.toggleMeridian();
    }
  }

  changeHours(step: number): void {
    if (
      (this.canIncrementHours && step > 0) ||
      (this.canDecrementHours && step < 0)
    ) {
      this.hours = this.loadHours(step);
      this.updateTime();
    }
  }

  loadHours(step): any {
    let _hours = this.hours;
    step = typeof step !== 'number' ? parseInt(step, 10) : step;
    const hours = parseInt(_hours, 10) + step;
    const prevValue = _hours;
    this.prevHourVal = prevValue;
    if (this.showMeridian) {
      if (hours <= 12 && hours >= 1) {
        _hours = padNumber(hours);
      } else if (hours > 12) {
        _hours = padNumber(hours - 12);
      } else if (hours < 1) {
        _hours = padNumber(12 + hours);
      }
    } else {
      if (hours <= 23 && hours >= 0) {
        _hours = padNumber(hours);
      } else if (hours > 23) {
        _hours = padNumber(hours - 24);
      } else if (hours < 0) {
        _hours = padNumber(24 + hours);
      }
    }
    this.updateMeridian(hours, prevValue, step);
    return _hours;
  }

  changeMinutes(step: number): void {
    if (
      (this.canIncrementMinutes && step > 0) ||
      (this.canDecrementMinutes && step < 0)
    ) {
      step = typeof step !== 'number' ? parseInt(step, 10) : step;
      const minutes = parseInt(this.minutes, 10) + step;
      if (
        (!this.canIncrementHours && step > 0) ||
        (!this.canDecrementHours && step < 0)
      ) {
        if (minutes < 0 && step < 0) {
          this._hours = this.loadHours(-1);
          const dateObj = new Date();
          dateObj.setHours(parseInt(this._hours, 10));
          if (this.min) {
            const _minvalue = parseTime(this.min);
            if (dateObj <= _minvalue) {
              this.canDecrementMinutes = false;
              return;
            }
          }
        }
        if (minutes > 59 && step > 0) {
          this._hours = this.loadHours(1);
          const dateObj = new Date();
          dateObj.setHours(parseInt(this._hours, 10));
          if (this.max) {
            const _maxvalue = parseTime(this.max);
            if (dateObj <= _maxvalue) {
              this.canIncrementMinutes = false;
              return;
            }
          }
        }
      }
      // step = typeof step !== 'number' ? parseInt(step, 10) : step;
      this.prevMinVal = minutes;
      if (minutes <= 59 && minutes >= 0) {
        this.minutes = padNumber(minutes);
      } else if (minutes > 59) {
        this.minutes = padNumber(minutes - 60);
        this.changeHours(1);
      } else if (minutes < 0) {
        this.minutes = padNumber(60 + minutes);
        this.changeHours(-1);
      }
      this.updateTime();
    }
  }

  changeSeconds(step: number): void {
    if (
      (this.canIncrementSeconds && step > 0) ||
      (this.canDecrementSeconds && step < 0)
    ) {
      step = typeof step !== 'number' ? parseInt(step, 10) : step;
      const seconds = parseInt(this.seconds, 10) + step;
      this.prevSecVal = seconds;
      if (seconds <= 59 && seconds >= 0) {
        this.seconds = padNumber(seconds);
      } else if (seconds > 59) {
        this.seconds = padNumber(seconds - 60);
        this.changeMinutes(1);
      } else if (seconds < 0) {
        this.seconds = padNumber(60 + seconds);
        this.changeMinutes(-1);
      }
      this.updateTime();
    }
  }

  updateHours(event: Event): void {
    let hours = (<HTMLTextAreaElement>event.target).value;
    this.hours = hours;
    this.updateTime();
  }

  updateMinutes(event: Event) {
    let minutes = (<HTMLTextAreaElement>event.target).value;
    this.minutes = minutes;
    this.updateTime();
  }

  updateSeconds(event: Event) {
    let seconds = (<HTMLTextAreaElement>event.target).value;
    this.seconds = seconds;
    this.updateTime();
  }

  updateTime() {
    if (!isInputValid(this.hours, this.minutes, this.seconds, this.isPM())) {
      this.onChange(null);
      return;
    }
    const _value = new Date();
    let hours = parseInt(this.hours, 10);
    if (this.isPM() && hours !== 12) {
      hours = 12 + parseInt(this.hours, 10);
    }
    this.modelValue = new Date(
      _value.getFullYear(),
      _value.getMonth(),
      _value.getDate(),
      hours,
      parseInt(this.minutes, 10),
      parseInt(this.seconds, 10)
    );
    this.onChange(this.modelValue);
    this.validateTime(this.modelValue);
  }

  updateOnClose() {
    this.selectionDone.emit(this.modelValue);
    this.timepickerService.inputElement.subscribe(result => {
      result['nativeElement'].children[0].children[0].focus(); // to focus on input element when input is pressed.
    });
  }

  toggleMeridian(): void {
    if (!this.showMeridian) {
      return;
    }
    if (this.isPM()) {
      this.meridian = this.meridians[0];
    } else {
      this.meridian = this.meridians[1];
    }
    this.updateTime();
  }

  /**
   * Write a new value to the element.
   */
  writeValue(obj: any): void { }

  /**
   * Set the function to be called when the control receives a change event.
   */
  registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  /**
   * Set the function to be called when the control receives a touch event.
   */
  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  private _renderTime(value: string | Date): void {
    if (!isValidDate(value)) {
      this.hours = '';
      this.minutes = '';
      this.seconds = '';
      this.meridian = this.meridians[0];

      return;
    }
    const _value = parseTime(value);
    const _hoursPerDayHalf = 12;
    let _hours = _value.getHours();

    if (this.showMeridian) {
      this.meridian = this.meridians[_hours >= _hoursPerDayHalf ? 1 : 0];
      _hours = _hours % _hoursPerDayHalf;
      // should be 12 PM, not 00 PM
      if (_hours === 0) {
        _hours = _hoursPerDayHalf;
      }
    }

    this.hours = padNumber(_hours);
    this.minutes = padNumber(_value.getMinutes());
    this.seconds = padNumber(_value.getUTCSeconds());
    this.modelValue = _value;
  }

  validateTime(value) {
    const _value = parseTime(value);
    const _hours = _value.getHours();
    const _minutes = _value.getMinutes();
    if (this.min) {
      const _minvalue = parseTime(this.min);
      const _minhours = _minvalue.getHours();
      const _minminutes = _minvalue.getMinutes();
      const _minseconds = _minvalue.getMinutes();
      if (_minvalue >= _value) {
        this.canDecrementHours = false;
        this.canDecrementMinutes = false;
        this.canDecrementSeconds = false;
      } else {
        if (_hours > _minhours) {
          this.canDecrementHours = true;
          this.canDecrementMinutes = true;
          this.canDecrementSeconds = true;
        } else if (_hours <= _minhours) {
          this.canDecrementHours = false;
          if (this.prevMinVal > _minminutes) {
            this.canDecrementMinutes = true;
          } else if (
            this.prevMinVal <= _minminutes &&
            this.prevHourVal === this.hours
          ) {
            this.canDecrementMinutes = false;
            if (this.prevSecVal > _minseconds) {
              this.canDecrementSeconds = true;
            } else if (this.prevSecVal <= _minseconds) {
              this.canDecrementSeconds = false;
            }
          }
        }
      }
    } else {
      this.canDecrementHours = true;
      this.canDecrementMinutes = true;
      this.canDecrementSeconds = true;
    }
    if (this.max) {
      const _maxvalue = parseTime(this.max);
      const _maxhours = _maxvalue.getHours();
      const _maxminutes = _maxvalue.getMinutes();
      const _maxseconds = _maxvalue.getMinutes();
      if (_maxvalue <= _value) {
        this.canIncrementHours = false;
        this.canIncrementMinutes = false;
        this.canIncrementSeconds = false;
      } else {
        if (_hours < _maxhours) {
          this.canIncrementHours = true;
          this.canIncrementMinutes = true;
          this.canIncrementSeconds = true;
        } else if (_hours >= _maxhours) {
          this.canIncrementHours = false;
          if (_minutes < _maxminutes) {
            this.canIncrementMinutes = true;
          } else if (_minutes >= _maxminutes) {
            this.canIncrementMinutes = false;
            if (this.prevSecVal < _maxseconds) {
              this.canIncrementSeconds = true;
            } else if (this.prevSecVal >= _maxseconds) {
              this.canIncrementSeconds = false;
            }
          }
        }
      }
    } else {
      this.canIncrementHours = true;
      this.canIncrementMinutes = true;
      this.canIncrementSeconds = true;
    }
  }

  // when escape key is pressed
  @HostListener('keyup', ['$event'])
  onKeyup(e: any) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      this.timepickerService.inputElement.subscribe(result => {
        result['nativeElement'].children[0].children[0].focus(); // to focus on input element.
      });
    }
  }

  clear() {
    this.modelValue = null;
    this.closeEvent.emit(this.modelValue);
    this.timepickerService.inputElement.subscribe(result => {
      result['nativeElement'].children[0].children[0].focus(); // to focus on input element when clear is pressed.
    });
  }
}
